import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchContent } from "@/lib/api";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/sections/Breadcrumbs";
import CtaBand from "@/components/sections/CtaBand";
import ProseBlock from "@/components/sections/ProseBlock";
import FaqAccordion from "@/components/sections/FaqAccordion";
import { BUSINESS, UI, SERVICES, serviceUrl, serviceTitle, trackEvent } from "@/content/site";

// Module-level constants for CtaBand fallbacks so JSX prop identity is stable across renders.
const BLOG_CTA_EN = {
  heading: "Speak with a Dallas Family Law Attorney",
  paragraph: "Free bilingual consultation — we respond within one business day.",
};
const BLOG_CTA_ES = {
  heading: "Hable con un Abogado de Familia en Dallas",
  paragraph: "Consulta gratis bilingüe — respondemos en un día hábil.",
};

export default function BlogPost() {
  const { pathname } = useLocation();
  const lang = useLang();
  const ui = UI[lang];
  const normalized = pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["content", normalized],
    queryFn: () => fetchContent(normalized),
    staleTime: 5 * 60_000,
  });

  useEffect(() => { window.scrollTo(0, 0); }, [normalized]);

  const sections = data?.page?.sections;

  const proseSections = useMemo(() => {
    if (!sections) return [];
    return sections.filter((s) => s.kind === "prose");
  }, [sections]);

  const faqSection = useMemo(() => {
    if (!sections) return null;
    return sections.find((s) => s.kind === "faq");
  }, [sections]);

  if (isLoading) return <div className="gw-container gw-section"><p>Loading…</p></div>;
  if (isError || !data?.page) {
    return (
      <div className="gw-container gw-section">
        <h1>{lang === "es" ? "Publicación no encontrada" : "Post not found"}</h1>
        <Link to={lang === "es" ? "/es/blog" : "/blog"} className="gw-btn gw-btn-primary">
          {lang === "es" ? "Ver todas las publicaciones" : "See all posts"}
        </Link>
      </div>
    );
  }

  const { page, schema, pair } = data;
  const meta = page.post_meta || {};
  const relatedService = meta.related_service_key ? SERVICES.find((s) => s.key === meta.related_service_key) : null;
  const showTOC = proseSections.length >= 4;

  const publishedFormatted = meta.datePublished
    ? new Date(meta.datePublished).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "long", day: "numeric" })
    : "";
  const modifiedFormatted = meta.dateModified && meta.dateModified !== meta.datePublished
    ? new Date(meta.dateModified).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <>
      <Seo page={page} path={normalized} lang={lang} pair={pair} schema={schema} />

      <article className="gw-container gw-section gw-blog-article">
        <Breadcrumbs items={page.breadcrumbs || []} lang={lang} />

        <div className="gw-blog-article-header">
          {meta.category && <span className="gw-blog-category-chip">{meta.category}</span>}
          {meta.viral && <span className="gw-viral-pill">{lang === "en" ? "Trending" : "En Tendencia"}</span>}
          <h1>{page.h1}</h1>
          <div className="gw-blog-byline">
            <div className="gw-blog-author-mini">
              <img
                className="gw-blog-author-avatar"
                src="/images/isabela-trench.jpeg"
                alt="Isabela A. Garcia"
                width="44"
                height="44"
                loading="lazy"
              />
              <div>
                <strong>
                  <Link to={ui.isabelaUrl}>Isabela A. Garcia</Link>
                </strong>
                <span>{lang === "en" ? "Founding Attorney, Garcia-Windsor, P.C." : "Abogada Fundadora, Garcia-Windsor, P.C."}</span>
              </div>
            </div>
            <div className="gw-blog-dates">
              <time dateTime={meta.datePublished}>
                {lang === "en" ? "Published " : "Publicado "}{publishedFormatted}
              </time>
              {modifiedFormatted && (
                <time dateTime={meta.dateModified}>
                  {lang === "en" ? " · Updated " : " · Actualizado "}{modifiedFormatted}
                </time>
              )}
            </div>
          </div>
        </div>

        {page.quotable_answer && (
          <aside
            className="gw-quotable gw-blog-takeaway speakable-answer"
            data-testid="answer-first-module"
            aria-label={lang === "en" ? "Key takeaways" : "Puntos clave"}
          >
            <strong>{lang === "en" ? "Key takeaways: " : "Puntos clave: "}</strong>
            <span>{page.quotable_answer}</span>
          </aside>
        )}

        {meta.pair_slug && (
          <aside className="gw-blog-language-callout">
            <span>{lang === "en" ? "Lea este artículo en español" : "Read this article in English"}</span>
            <Link to={pair} onClick={() => trackEvent("es_toggle_click", { source: "blog_post", from: lang })}>
              {lang === "en" ? "→ Español" : "→ English"}
            </Link>
          </aside>
        )}

        {meta.viral && (
          <aside className="gw-blog-viral-callout">
            <div>
              <strong>{lang === "en" ? "Facing a child support issue in Dallas?" : "¿Enfrenta un problema de manutención en Dallas?"}</strong>
              <p>
                {lang === "en"
                  ? "Speak with a bilingual Dallas child support attorney. Free consultation."
                  : "Hable con un abogado bilingüe de manutención en Dallas. Consulta gratis."}
              </p>
            </div>
            <div className="gw-blog-viral-buttons">
              <Link to={lang === "en" ? "/family-law/child-support" : "/es/derecho-de-familia/manutencion-de-los-hijos"} className="gw-btn gw-btn-primary">
                {lang === "en" ? "Dallas child support lawyers" : "Abogados de manutención de hijos en Dallas"}
              </Link>
              <Link to={lang === "en" ? "/family-law/divorce-laws" : "/es/derecho-de-familia/divorcio"} className="gw-btn gw-btn-ghost">
                {lang === "en" ? "Dallas divorce lawyers" : "Abogados de divorcio en Dallas"}
              </Link>
            </div>
          </aside>
        )}

        {showTOC && (
          <nav className="gw-blog-toc" aria-label={lang === "en" ? "Table of contents" : "Tabla de contenido"}>
            <strong>{lang === "en" ? "On this page" : "En esta página"}</strong>
            <ol>
              {proseSections.map((s, i) => (
                <li key={s.heading || `toc-${i}`}>
                  <a href={`#s${i}`}>{s.heading}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="gw-blog-body">
          {proseSections.map((section, i) => (
            <section id={`s${i}`} key={section.heading || `sec-${i}`}>
              <ProseBlock section={section} />
              {i === Math.floor(proseSections.length * 0.4) && relatedService && (
                <aside className="gw-blog-inline-cta gw-card">
                  <span className="gw-eyebrow">{lang === "en" ? "Related service" : "Servicio relacionado"}</span>
                  <h3>{lang === "en" ? `Need help with ${serviceTitle(relatedService, lang).toLowerCase()}?` : `¿Necesita ayuda con ${serviceTitle(relatedService, lang).toLowerCase()}?`}</h3>
                  <p>{lang === "en" ? "Talk to a bilingual Dallas family lawyer — free consultation." : "Hable con un abogado bilingüe de familia en Dallas — consulta gratis."}</p>
                  <Link to={serviceUrl(relatedService, lang)} className="gw-btn gw-btn-primary">
                    {serviceTitle(relatedService, lang)} →
                  </Link>
                </aside>
              )}
            </section>
          ))}

          {faqSection && <FaqAccordion section={faqSection} />}
        </div>

        <div className="gw-blog-author-box gw-card">
          <img
            className="gw-blog-author-avatar-lg"
            src="/images/isabela-trench.jpeg"
            alt="Isabela A. Garcia"
            width="88"
            height="88"
            loading="lazy"
          />
          <div>
            <span className="gw-eyebrow">{lang === "en" ? "About the author" : "Sobre la autora"}</span>
            <h3>Isabela A. Garcia</h3>
            <p>
              {lang === "en"
                ? "Bilingual Dallas family law attorney, founding attorney of Garcia-Windsor, P.C., Super Lawyers Rising Star, Top 50 Women Leaders in Texas, and exclusive consulting attorney for the Mexican Consulate General of Dallas (PALE program)."
                : "Abogada bilingüe de familia en Dallas, abogada fundadora de Garcia-Windsor, P.C., Super Lawyers Rising Star, Top 50 Mujeres Líderes de Texas, y abogada consultora exclusiva del Consulado General de México en Dallas (programa PALE)."}
            </p>
            <div style={{display: "flex", gap: 12, marginTop: 8}}>
              <Link to={ui.isabelaUrl}>{lang === "en" ? "Full bio →" : "Biografía completa →"}</Link>
              <a href={BUSINESS.social.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn ↗</a>
            </div>
          </div>
        </div>

        <p className="gw-muted" style={{fontSize: "0.82rem", marginTop: 24, fontStyle: "italic"}}>
          {lang === "en"
            ? "Legal disclaimer: The information in this post is for general informational purposes only and does not constitute legal advice. Reading this post does not create an attorney-client relationship. Consult a licensed Texas attorney about your specific situation."
            : "Aviso legal: La información en esta publicación es solo para fines informativos generales y no constituye asesoría legal. Leer esta publicación no crea una relación abogado-cliente. Consulte a un abogado con licencia de Texas sobre su situación específica."}
        </p>
      </article>

      <CtaBand section={lang === "en" ? BLOG_CTA_EN : BLOG_CTA_ES} lang={lang} />

      <style>{`
        .gw-blog-article { max-width: 820px; }
        .gw-blog-article-header h1 { font-size: clamp(2rem, 4vw, 2.75rem); margin: var(--gw-space-3) 0; }
        .gw-blog-category-chip { display: inline-block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--gw-accent-hover); background: #FFF9E8; padding: 4px 10px; border-radius: 999px; }
        .gw-viral-pill { display: inline-block; margin-left: 8px; padding: 3px 12px; background: var(--gw-accent); color: var(--gw-primary-dark); font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border-radius: 999px; vertical-align: middle; }
        .gw-blog-byline { display: flex; justify-content: space-between; align-items: center; gap: var(--gw-space-4); margin-top: var(--gw-space-4); padding: var(--gw-space-4) 0; border-top: 1px solid var(--gw-border); border-bottom: 1px solid var(--gw-border); flex-wrap: wrap; }
        .gw-blog-author-mini { display: flex; align-items: center; gap: 12px; }
        .gw-blog-author-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; object-position: center top; display: block; border: 2px solid var(--gw-accent); }
        .gw-blog-author-mini strong { display: block; color: var(--gw-primary-dark); font-family: var(--gw-font-serif); }
        .gw-blog-author-mini span { color: var(--gw-muted); font-size: 0.85rem; }
        .gw-blog-dates { color: var(--gw-muted); font-size: 0.85rem; }
        .gw-blog-takeaway { background: #FFF9E8; }
        .gw-blog-language-callout { display: flex; justify-content: space-between; align-items: center; gap: 12px; background: var(--gw-primary-dark); color: #FFF9E8; padding: var(--gw-space-4) var(--gw-space-5); border-radius: var(--gw-radius); margin: var(--gw-space-5) 0; flex-wrap: wrap; }
        .gw-blog-language-callout a { color: var(--gw-accent); font-weight: 700; text-decoration: none; }
        .gw-blog-language-callout a:hover { color: #FFE799; }
        .gw-blog-viral-callout { display: grid; grid-template-columns: 1fr auto; gap: var(--gw-space-4); align-items: center; background: linear-gradient(135deg, #FFF4CF 0%, #F1C232 100%); padding: var(--gw-space-6); border-radius: var(--gw-radius-lg); margin: var(--gw-space-6) 0; border-left: 5px solid var(--gw-primary); }
        .gw-blog-viral-callout strong { color: var(--gw-primary-dark); font-family: var(--gw-font-serif); font-size: 1.2rem; }
        .gw-blog-viral-callout p { color: var(--gw-primary-dark); margin: 6px 0 0; }
        .gw-blog-viral-buttons { display: flex; flex-direction: column; gap: 8px; }
        @media (max-width: 720px) {
          .gw-blog-viral-callout { grid-template-columns: 1fr; }
        }
        .gw-blog-toc { background: #F5EFE3; border-radius: var(--gw-radius); padding: var(--gw-space-5); margin: var(--gw-space-6) 0; }
        .gw-blog-toc strong { display: block; font-family: var(--gw-font-serif); color: var(--gw-primary-dark); margin-bottom: 12px; }
        .gw-blog-toc ol { margin: 0; padding-left: 20px; }
        .gw-blog-toc li { margin-bottom: 6px; }
        .gw-blog-toc a { color: var(--gw-primary); }
        .gw-blog-body h2 { margin-top: var(--gw-space-8); }
        .gw-blog-inline-cta { background: linear-gradient(135deg, #7A1B2A 0%, #3B0A14 100%); color: #FFF9E8; margin: var(--gw-space-6) 0; padding: var(--gw-space-6); border-color: transparent; }
        .gw-blog-inline-cta h3 { color: var(--gw-accent); margin: 8px 0; }
        .gw-blog-inline-cta p { color: rgba(255,255,255,0.9); margin-bottom: 16px; }
        .gw-blog-inline-cta .gw-eyebrow { color: var(--gw-accent); }
        .gw-blog-inline-cta .gw-btn-primary { background: var(--gw-accent); color: var(--gw-primary-dark); }
        .gw-blog-author-box { display: grid; grid-template-columns: auto 1fr; gap: var(--gw-space-4); margin-top: var(--gw-space-8); align-items: flex-start; }
        .gw-blog-author-avatar-lg { width: 88px; height: 88px; border-radius: 50%; object-fit: cover; object-position: center top; display: block; border: 3px solid var(--gw-accent); flex-shrink: 0; }
        .gw-blog-author-box h3 { margin: 4px 0 8px; }
      `}</style>
    </>
  );
}
